import { TimecardGroupTotalsDto } from 'types/timecards';

/** What `GET /v1/timecards/group-totals` is asked for: the list's filter and grouping, and the groups on screen. */
export interface TimecardGroupTotalsArgs {
    filters: string;
    /** The backend's grouping name: DATE, AUTHOR, CASE or CLIENT. */
    groupBy: string;
    /** The keys of the groups the loaded rows belong to, as `timecardGroupKeyOf` derives them. */
    groupKeys: readonly string[];
}

/**
 * The RSQL selector whose value is a grouping's group key — the same field `timecardGroupKeyOf` reads
 * off a row, so a key taken from a row selects exactly that row's group server-side.
 */
const GROUP_KEY_SELECTOR: Record<string, string> = {
    DATE: 'timecardDate',
    AUTHOR: 'employee.id',
    CASE: 'matter.id',
    CLIENT: 'matter.clientParty.id',
};

/**
 * The filter a group-totals request sends: the list's own filter, narrowed to the groups on screen.
 *
 * A ribbon covers its WHOLE group across every page, so the narrowing is by group key, never by row
 * id — but only for the groups the reader has rows of. Without it the request sums every group of the
 * filtered set, which on a firm-wide list is every timecard of the tenant, for a page that shows a
 * handful of days.
 *
 * A row without a key (no author) has no group to select; such a group is left to the sums of the rows
 * in hand.
 */
export function timecardGroupTotalsFilter({ filters, groupBy, groupKeys }: TimecardGroupTotalsArgs): string {
    const selector = GROUP_KEY_SELECTOR[groupBy];
    const keys = groupKeys.filter((key) => key !== '');
    if (!selector || keys.length === 0) {
        return filters;
    }
    const groupPredicate = `${selector}=in=(${keys.join(',')})`;
    // Parenthesised because RSQL's `,` (OR) binds looser than `;` (AND): a filter with a top-level OR
    // would otherwise take the predicate into its last branch only.
    return filters ? `(${filters});${groupPredicate}` : groupPredicate;
}

/** The cache-entry key: one entry per filter and grouping, whatever groups a request asks for. */
export function timecardGroupTotalsCacheKey(
    endpointName: string,
    { filters, groupBy }: Pick<TimecardGroupTotalsArgs, 'filters' | 'groupBy'>
): string {
    return `${endpointName}(${JSON.stringify({ filters, groupBy })})`;
}

/**
 * The groups one request asks for. Normally only those the cache holds no totals for — the groups
 * the reader scrolled past keep theirs, and summing them again is work for nothing. A forced fetch
 * (a `TimecardsTotals` invalidation after a billability or matter switch) asks for every loaded
 * group, because the cached figures are exactly what went stale.
 */
export function groupKeysToRequest(
    loaded: readonly string[],
    cached: readonly TimecardGroupTotalsDto[] | undefined,
    forced: boolean
): string[] {
    if (forced || !cached) {
        return [...loaded];
    }
    const have = new Set(cached.map((totals) => totals.groupKey));
    return loaded.filter((key) => !have.has(key));
}

/**
 * The cache after an answer: every group the answer names takes its new totals, every other group
 * keeps what it had. The reader scrolls one page at a time, and a group they passed must keep its
 * ribbon while the next page's groups are being fetched.
 */
export function mergeTimecardGroupTotals(
    cached: TimecardGroupTotalsDto[],
    incoming: TimecardGroupTotalsDto[]
): TimecardGroupTotalsDto[] {
    const incomingKeys = new Set(incoming.map((totals) => totals.groupKey));
    return [...cached.filter((totals) => !incomingKeys.has(totals.groupKey)), ...incoming];
}

/** Whether a request has to go out: only once the page holds a group nobody asked for yet. */
export function hasNewGroupKeys(
    current: readonly string[] | undefined,
    previous: readonly string[] | undefined
): boolean {
    const requested = new Set(previous ?? []);
    return (current ?? []).some((key) => !requested.has(key));
}
